// LogMiner GET APIs
//
// https://logminer-backend-staging.ml.motional.com/swagger/index.html

import { REQ_STATUS } from 'packages/lib/common-types/types';
import {
  authBlobFetcher,
  swrFetcherLogminer,
  SWRFetcherOptionsLoose,
} from 'packages/lib/utils/fetch';
import useSWR, { KeyedMutator } from 'swr';
import {
  isJobStatusArray,
  QueryImageMetaData,
  JobStatus,
} from 'packages/lib/api/logminer/contract/log-miner-types';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useMapState } from 'packages/lib/utils/hooks/use-map-state';
import { isLocalhost } from 'packages/main/serviceWorkerRegistration';

const USE_JPEG_ON_LOCALHOST = true; // Not much webp on localhost for some queries
const DEFAULT_ASPECT_RATIO = 1.78;

export interface GetJobsStatusesURLParams {
  userEmail?: string;
  logName?: string;
  jobName?: string;
  timestamp?: string;
}

// Loads just one job status (any user) for a specific logName/timestamp combo
export const useJobStatusAPI = (
  logName: string,
  timestamp?: string,
  abortController?: AbortController
): {
  jobStatus: JobStatus | null;
  error: string | null;
  isLoading: boolean;
  refetch: KeyedMutator<JobStatus[]>;
} => {
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const ready = timestamp !== undefined;

  const {
    data: jobStatuses,
    isLoading,
    error,
    refetch,
  } = useJobsStatusesAPI({
    ready,
    logName,
    timestamp,
    abortController,
  });

  useEffect(() => {
    setJobStatus(
      jobStatuses && jobStatuses.length > 0 && !isLoading && !error
        ? jobStatuses[0]
        : null
    );
  }, [jobStatuses, isLoading, error]);

  return {
    jobStatus,
    isLoading,
    error,
    refetch,
  };
};

export function getJobsStatusesURL(params: GetJobsStatusesURLParams): string {
  const { userEmail, logName, jobName, timestamp } = params;
  const qp = new URLSearchParams({
    ...(userEmail && { userName: userEmail }),
    ...(logName && { logName }),
    ...(jobName && { jobName }),
    ...(timestamp && { timestamp }),
    pageSize: '10000',
  } as Record<string, string>);

  return `/jobs-status?${qp.toString()}`;
}

// Consider switching to useAsset() in the future:  https://github.com/pmndrs/use-asset
// See: packages/main/components/lidar-tool/stores/cached-assets.ts
export const useLogMinerImageAPI = (
  imageMetaData: QueryImageMetaData | null | undefined,
  loadAspectRatio: boolean,
  abortController?: AbortController
): {
  imageSrc: string | null;
  status: REQ_STATUS;
  error: string | null;
  aspectRatio: number;
} => {
  const [aspectRatio, setAspectRatio] = useState<number>(DEFAULT_ASPECT_RATIO);
  const [cachedImages, addImageToCache] = useMapState<string, string>(
    new Map()
  );

  let qp: URLSearchParams | null = null;
  let urlBase = '';
  if (imageMetaData) {
    urlBase = '/images';

    qp = new URLSearchParams({
      logName: imageMetaData.log_name,
      timestamp: imageMetaData.timestamp.toString(),
      channel: imageMetaData.camera_channel,
      imageType: isLocalhost && USE_JPEG_ON_LOCALHOST ? 'jpeg' : 'webp', // Almost no webp data on localhost, so use jpeg in that case
      downloadImage: 'yes',
    } as Record<string, string>);
  }

  const url = qp ? `${urlBase}?${qp.toString()}` : null;
  const imageIsInCache = !!(url ? cachedImages.has(url) : false);

  const {
    data: blob,
    error,
    isValidating,
  } = useSWR<Blob>(
    () => (imageIsInCache ? null : url),
    (url: string) =>
      authBlobFetcher(url, {
        baseURL: 'logMiner',
        ...(abortController && { signal: abortController.signal }),
      }),
    { revalidateOnFocus: false }
  );

  const imgSrc = blob && !isValidating ? URL.createObjectURL(blob) : '';

  useEffect(() => {
    if (!isValidating && blob && url && !imageIsInCache) {
      addImageToCache(url, imgSrc);
    }
  }, [blob, isValidating, url, imgSrc, imageIsInCache, addImageToCache]);

  if (imageIsInCache && url)
    return {
      imageSrc: cachedImages.get(url) as string, // key guaranteed to exist if map.has() is true
      error: null,
      status: REQ_STATUS.success,
      aspectRatio,
    };

  if (loadAspectRatio && imgSrc)
    loadImageAspectRatio(imgSrc, setAspectRatio, aspectRatio);

  const returnStatus = isValidating ? REQ_STATUS.pending : REQ_STATUS.success;

  return {
    imageSrc: imgSrc ?? '',
    error: error?.message ?? null,
    status: error?.message ? REQ_STATUS.error : returnStatus,
    aspectRatio,
  };
};

function loadImageAspectRatio(
  newImage: string,
  setAspectRatio: Dispatch<SetStateAction<number>>,
  aspectRatio: number
): void {
  const img = document.createElement('img');
  img.src = newImage;

  img.onload = (event): void => {
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setAspectRatio(img.naturalWidth / img.naturalHeight);
    }
  };
}

interface UseJobsStatusesAPIParams {
  ready?: boolean; // defaults to true if not set
  userEmail?: string;
  logName?: string;
  jobName?: string;
  timestamp?: string;
  abortController?: AbortController;
  fetcher?: <T>(url: string, options?: SWRFetcherOptionsLoose<T>) => Promise<T>;
}

/**  When something is submitted to be annoated (GO, Lidar, etc.) the backend automatically puts it in a jobs-status list.
// We can determine what has already been sent, here, as well as its success/fail/pending status, who sent it, when, etc.
// @param ready set to false to prevent backend API calls
// */
export const useJobsStatusesAPI = ({
  ready = true,
  userEmail,
  logName,
  jobName,
  timestamp,
  abortController,
  fetcher = swrFetcherLogminer,
}: UseJobsStatusesAPIParams): {
  data: JobStatus[] | null;
  error: string | null;
  isLoading: boolean;
  refetch: KeyedMutator<JobStatus[]>;
} => {
  const queryURL = getJobsStatusesURL({
    logName,
    jobName,
    timestamp,
    userEmail,
  });
  const { data, error, mutate, isLoading } = useSWR(
    () => (ready ? queryURL : null),
    (url: string) =>
      fetcher<JobStatus[]>(url, {
        typeGuard: isJobStatusArray,
        ...(abortController && { signal: abortController.signal }),
      })
  );

  // Fix 'payload' field in response, which we want to be an object, not a JSON string
  useEffect(() => {
    if (data && !isLoading) {
      data.forEach((item, idx) => {
        try {
          if (typeof item.payload === 'string')
            item.payload = JSON.parse(item.payload);
        } catch (e) {
          item.payload = {
            errorMsg: 'Failed to parse JSON payload. Invalid JSON.',
          };
          console.warn(
            'useJobsStatusesAPI: JSON payload failed to parse at index',
            idx,
            item.payload
          );
        }
      });
    }
  }, [data, isLoading]);

  return {
    data: data ?? null,
    error: error?.message ?? null,
    isLoading,
    refetch: mutate,
  };
};
